import React, { Component } from "react";
import { loading, t, alert } from "../components/CustomComponent.js";
import {
  WORK_REQUEST_UPLOAD_RULES,
  pickFilesByRule,
  compressImageFiles,
  formatText,
  formatFileSize,
  toMB,
} from "../utils/fileUpload";

// หน้าแจ้งซ่อมสาธารณะ (scan QR จากสติกเกอร์ที่ติดกับ asset) - ไม่ต้องล็อกอิน ไม่ต้องมี company-uid
// จึงเรียก fetch ตรง ๆ แทน GET/POST ของ services/api.js (ตัวนั้นแนบ Authorization/company-uid
// อัตโนมัติ และเจอ token/expired จะสั่ง localStorage.clear() + เด้งไปหน้า login ซึ่งไม่เข้ากับหน้านี้)
const API_BASE = process.env.REACT_APP_API_URL;
const DESC_MIN_LENGTH = 10;
const DESC_MAX_LENGTH = 500;
const PHONE_MIN_LENGTH = 9;
const IMAGE_RULE = WORK_REQUEST_UPLOAD_RULES.image;
const VIDEO_RULE = WORK_REQUEST_UPLOAD_RULES.video;

// backend ปฏิเสธไฟล์ที่ mime ว่าง - มือถือบางรุ่นส่ง file.type ว่าง (pickFilesByRule ยอมให้ผ่านด้วยนามสกุล)
// จึงเดา mime จากนามสกุลให้ก่อนอัปโหลด
const MIME_BY_EXT = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif", webp: "image/webp",
  bmp: "image/bmp", heic: "image/heic", heif: "image/heif",
  mp4: "video/mp4", mov: "video/quicktime", m4v: "video/x-m4v", webm: "video/webm", avi: "video/x-msvideo", mkv: "video/x-matroska",
};
const resolveMime = (file, fileType) => {
  if (file?.type) return file.type;
  const ext = (file?.name || "").split(".").pop()?.toLowerCase();
  return MIME_BY_EXT[ext] || (fileType === "video" ? "video/mp4" : "image/jpeg");
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("read file failed"));
    reader.readAsDataURL(file);
  });

// อัปโหลดไฟล์แบบสาธารณะ (ไม่ใช้ UPLOAD ของ services/api.js เพราะแนบ token และเด้ง login ตอน 401)
// 1) presign แล้ว PUT ตรง 2) ล้มเหลวค่อย fallback base64 ผ่าน auth/upload · คืน {file_path, mime_type} หรือ null
const publicUpload = async (file, folder, mime) => {
  try {
    const presign = await publicFetch("auth/upload/presign", {
      method: "POST",
      body: { file_name: file.name, mime_type: mime, folder, file_size: file.size },
    });
    const info = presign.body?.data;
    if (presign.ok && presign.body?.status === true && info?.upload_url && info?.public_url) {
      const headers = { "Content-Type": info.mime_type || mime };
      if (info.content_disposition) headers["Content-Disposition"] = info.content_disposition;
      const putRes = await fetch(info.upload_url, { method: "PUT", headers, body: file });
      if (putRes.ok) return { file_path: info.public_url, mime_type: info.mime_type || mime };
    }
  } catch (e) {
    // ไป fallback
  }

  try {
    const base64 = await fileToBase64(file);
    const res = await publicFetch("auth/upload", { method: "POST", body: { file: base64, file_name: file.name, folder } });
    if (res.ok && res.body?.status === true && res.body?.data?.path) {
      return { file_path: res.body.data.path, mime_type: res.body.data.mime_type || mime };
    }
  } catch (e) {
    // ตกลงไป null
  }
  return null;
};

const publicFetch = async (path, { method = "GET", body } = {}) => {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { "Content-Type": "application/json", accept: "application/json" },
      cache: "no-cache",
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    let json = null;
    try {
      json = await res.json();
    } catch (e) {
      json = null;
    }
    return { ok: res.ok, status: res.status, body: json };
  } catch (e) {
    return { ok: false, status: 0, body: null };
  }
};

// ดึง token จาก path /repair-request/:token ตรง ๆ - หน้านี้เป็น class component
// และ repo นี้ไม่มีการใช้ useParams ที่ไหนเลย (ดู CLAUDE.md/notes) จึงอ่านจาก window.location เอง
const getTokenFromPath = () => {
  const match = window.location.pathname.match(/\/repair-request\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : "";
};

export default class RepairRequestPublic extends Component {
  constructor(props) {
    super(props);
    this.state = {
      token: "",
      // loading | invalid | pending | form | success
      view_state: "loading",

      asset: null,
      customer: null,
      company: null,
      categories: [],
      service_types: [],

      pending_wr_number: "",

      create_category_id: "",
      create_service_type: null,
      create_problem_desc: "",
      create_reporter_name: "",
      create_reporter_phone: "",

      images: [],
      videos: [],
      upload_progress: "",

      submitting: false,
      created_wr_number: "",
    };
  }

  componentWillUnmount() {
    this.revokeItems([...this.state.images, ...this.state.videos]);
  }

  revokeItems = (items) => {
    (items || []).forEach((item) => {
      if (item?.url && item.url.startsWith("blob:")) URL.revokeObjectURL(item.url);
    });
  };

  mapFilesToItems = (files) =>
    files.map((file) => ({ file, name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file) }));

  notifyRejectedFiles = ({ wrongType, tooBig, overLimit, rule }, fileType) => {
    const messages = [];
    if (wrongType.length > 0) messages.push(`${t("file_type_not_allowed")}: ${wrongType.join(", ")}`);
    if (tooBig.length > 0) messages.push(`${t("file_too_large")} (${t("max")} ${toMB(rule.maxSize)} MB): ${tooBig.join(", ")}`);
    if (overLimit > 0) {
      const key = fileType === "video" ? "max_videos_reached" : "max_images_reached";
      messages.push(formatText(t(key), { count: rule.maxFiles, excess: overLimit }));
    }
    if (messages.length > 0) alert("", "warning", t("warning"), messages.join(" · "));
  };

  // fileType: "image" | "video" - ตรวจชนิด/ขนาด/จำนวนตามกติกากลาง แล้วบีบอัดรูป (1920px) ก่อนเก็บ
  addFiles = async (fileList, fileType) => {
    if (this.state.submitting) return;
    const stateKey = fileType === "video" ? "videos" : "images";
    const current = this.state[stateKey] || [];

    const result = pickFilesByRule(fileList, fileType, current.length);
    this.notifyRejectedFiles(result, fileType);
    if (result.accepted.length === 0) return;

    let files = result.accepted;
    if (fileType === "image") {
      loading(true);
      try {
        files = await compressImageFiles(result.accepted);
      } finally {
        loading(false);
      }
    }

    const newItems = this.mapFilesToItems(files);
    this.setState((prev) => ({ [stateKey]: [...prev[stateKey], ...newItems] }));
  };

  handleDragOver = (e) => e.preventDefault();

  // ref ของ input[type=file] แยกตาม fileType (image/video) ให้กดตรงไหนของกล่องก็เปิดเลือกไฟล์ได้
  fileInputs = {};

  openFilePicker = (fileType) => {
    const list = fileType === "video" ? this.state.videos : this.state.images;
    const rule = fileType === "video" ? VIDEO_RULE : IMAGE_RULE;
    if (this.state.submitting || list.length >= rule.maxFiles) return;
    this.fileInputs[fileType]?.click();
  };

  handleBrowse = (e, fileType) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) this.addFiles(files, fileType);
  };

  handleDrop = (e, fileType) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) {
      const files = Array.from(e.dataTransfer.files);
      this.addFiles(files, fileType);
    }
  };

  removeFile = (index, fileType) => {
    if (this.state.submitting) return;
    const stateKey = fileType === "video" ? "videos" : "images";
    this.setState((prev) => {
      const list = [...prev[stateKey]];
      const [removed] = list.splice(index, 1);
      this.revokeItems([removed]);
      return { [stateKey]: list };
    });
  };

  // อัปโหลดทีละไฟล์ - คืน array ของ attachment (sequence 1..n) หรือ null ถ้าไฟล์ใดล้ม
  uploadGroup = async (items, folder, fileType, progress) => {
    const rule = fileType === "video" ? VIDEO_RULE : IMAGE_RULE;
    const out = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.file.size > rule.maxSize) {
        alert("", "warning", t("warning"), `${t("file_too_large")} (${t("max")} ${toMB(rule.maxSize)} MB): ${item.name || ""}`);
        return null;
      }
      progress.done += 1;
      this.setState({ upload_progress: formatText(t("repair_request_uploading"), { current: progress.done, total: progress.total }) });

      const mime = resolveMime(item.file, fileType);
      const res = await publicUpload(item.file, folder, mime);
      if (!res) {
        alert("", "warning", t("warning"), `${t("upload")} "${item.name || ""}" ${t("fail")}`);
        return null;
      }
      out.push({
        file_path: res.file_path,
        file_name: item.name || null,
        file_type: fileType,
        mime_type: res.mime_type || mime,
        file_size: item.size ?? null,
        sequence: i + 1,
      });
    }
    return out;
  };

  async componentDidMount() {
    const token = getTokenFromPath();
    this.setState({ token });

    if (!token) {
      this.setState({ view_state: "invalid" });
      loading(false);
      return;
    }

    loading(true);
    const result = await publicFetch(`public/asset-request/${encodeURIComponent(token)}`);
    loading(false);

    if (!result.ok || result.body?.status !== true) {
      this.setState({ view_state: "invalid" });
      return;
    }

    const data = result.body.data || {};

    if (data.can_submit === false) {
      this.setState({
        view_state: "pending",
        asset: data.asset || null,
        company: data.company || null,
        pending_wr_number: data.pending_request?.wr_number || "",
      });
      return;
    }

    this.setState({
      view_state: "form",
      asset: data.asset || null,
      customer: data.customer || null,
      company: data.company || null,
      categories: data.masters?.categories || [],
      service_types: data.masters?.service_types || [],
    });
  }

  getPendingMessage = () => {
    const { pending_wr_number, company } = this.state;
    return formatText(t("repair_request_pending_message"), {
      wr_number: pending_wr_number || "-",
      phone: company?.phone || "-",
    });
  };

  handleSubmit = async () => {
    const {
      token, create_category_id, create_problem_desc,
      create_reporter_name, create_reporter_phone, create_service_type,
    } = this.state;

    if (!create_category_id || !create_problem_desc.trim() || !create_reporter_name.trim() || !create_reporter_phone.trim()) {
      alert("", "warning", t("warning"), t("required_fields"));
      return;
    }

    const descLength = create_problem_desc.trim().length;
    if (descLength < DESC_MIN_LENGTH || descLength > DESC_MAX_LENGTH) {
      alert("", "warning", t("warning"), t("repair_request_desc_length_invalid"));
      return;
    }

    // backend ปฏิเสธเบอร์ที่สั้นกว่า 9 ตัวอักษร - เช็คก่อนส่งให้ผู้ใช้เห็นสาเหตุชัดเจน
    if (create_reporter_phone.trim().length < PHONE_MIN_LENGTH) {
      alert("", "warning", t("warning"), t("repair_request_phone_invalid"));
      return;
    }

    if (this.state.submitting) return;
    this.setState({ submitting: true, upload_progress: "" });
    loading(true);

    const { images, videos } = this.state;
    const progress = { done: 0, total: images.length + videos.length };
    const imageAtt = await this.uploadGroup(images, "work-requisitions/images", "image", progress);
    const videoAtt = imageAtt ? await this.uploadGroup(videos, "work-requisitions/videos", "video", progress) : null;
    if (!imageAtt || !videoAtt) {
      // อัปโหลดไฟล์ใดล้ม -> หยุด ไม่ส่งใบ (alert แจ้งชื่อไฟล์ไปแล้ว) ผู้ใช้กดส่งใหม่ได้
      loading(false);
      this.setState({ submitting: false, upload_progress: "" });
      return;
    }
    const attachments = [...imageAtt, ...videoAtt];

    const result = await publicFetch(`public/asset-request/${encodeURIComponent(token)}`, {
      method: "POST",
      body: {
        token,
        work_requisition_categories_id: create_category_id,
        work_requisition_service_types_id: create_service_type?.uid || null,
        problem_description: create_problem_desc.trim(),
        requester_name: create_reporter_name.trim(),
        requester_phone: create_reporter_phone.trim(),
        ...(attachments.length > 0 ? { attachments } : {}),
      },
    });

    loading(false);
    this.setState({ submitting: false, upload_progress: "" });

    if (result.ok && result.body?.status === true) {
      this.revokeItems([...images, ...videos]);
      this.setState({ images: [], videos: [], view_state: "success", created_wr_number: result.body.data?.wr_number || "" });
      return;
    }

    // มีคนแจ้งตัดหน้าไประหว่างที่ฟอร์มเปิดอยู่ - แสดงข้อความเดียวกับกรณีมีคำร้องค้างอยู่แล้ว
    if (result.status === 409) {
      this.setState({
        view_state: "pending",
        pending_wr_number: result.body?.errors?.wr_number || "",
      });
      return;
    }

    // 400 จาก validation มีแค่ข้อความ "Validation failed" (อังกฤษ) - ใช้ข้อความไทยที่บอกว่าให้ตรวจข้อมูลแทน
    // 429 ใช้ข้อความจาก backend ตรง ๆ · ต่อเน็ตไม่ได้ (status 0) / ระบบล่ม (5xx หรือ response ไม่ใช่ JSON) แยกข้อความให้ชัด
    let message = result.body?.message;
    if (result.status === 400) message = t("repair_request_submit_invalid");
    else if (result.status === 0) message = t("repair_request_submit_network_error");
    else if (!message || result.status >= 500) message = t("repair_request_submit_server_error");
    alert("", "warning", t("warning"), message);
  };

  renderAssetCard = () => {
    const { asset, customer } = this.state;
    if (!asset) return null;

    return (
      <div className="bg-white rounded-4 shadow-sm p-3 mb-3">
        <div className="text-secondary text-12 mb-2">{t("repair_request_asset_info")}</div>
        <div className="fw-bold text-16 text-dark mb-1">{asset.name || asset.asset_code || "-"}</div>
        <div className="text-13 text-secondary">{t("label_asset_code")}: {asset.asset_code || "-"}</div>
        {asset.serial_number ? <div className="text-13 text-secondary">{t("serial_number")}: {asset.serial_number}</div> : null}
        {asset.location ? <div className="text-13 text-secondary">{t("location")}: {asset.location}</div> : null}
        {customer ? (
          <div className="text-13 text-secondary">{t("customer")}: {customer.full_name_th || customer.full_name_en || "-"}</div>
        ) : null}
      </div>
    );
  };

  renderInvalid = () => (
    <div className="bg-white rounded-4 shadow-sm p-4 text-center">
      <div className="text-secondary text-40 mb-3 icon">{""}</div>
      <div className="fw-bold text-dark mb-2">{t("repair_request_invalid_title")}</div>
      <div className="text-secondary text-14">{t("repair_request_invalid_desc")}</div>
    </div>
  );

  renderPending = () => (
    <>
      {this.renderAssetCard()}
      <div className="bg-white rounded-4 shadow-sm p-4 text-center">
        <div className="text-warning text-40 mb-3 icon">{""}</div>
        <div className="fw-bold text-dark mb-2">{t("repair_request_pending_title")}</div>
        {/* ข้อความมี \n คั่นประโยค - เบราว์เซอร์ตัดบรรทัดภาษาไทยกลางคำได้ (เจอ "เจ้า/หน้าที่" บนจอมือถือ) จึงกำหนดจุดตัดเอง */}
        <div className="text-secondary text-14" style={{ whiteSpace: "pre-line" }}>{this.getPendingMessage()}</div>
      </div>
    </>
  );

  renderSuccess = () => (
    <div className="bg-white rounded-4 shadow-sm p-4 text-center">
      <div className="text-success text-40 mb-3 icon">{""}</div>
      <div className="fw-bold text-dark mb-2">{t("repair_request_success_title")}</div>
      <div className="text-secondary text-14" style={{ whiteSpace: "pre-line" }}>
        {formatText(t("repair_request_success_desc"), { wr_number: this.state.created_wr_number || "-" })}
      </div>
    </div>
  );

  renderDropzone = (fileType) => {
    const rule = fileType === "video" ? VIDEO_RULE : IMAGE_RULE;
    const hintKey = fileType === "video" ? "upload_hint_videos" : "upload_hint_images";
    const list = fileType === "video" ? this.state.videos : this.state.images;
    const full = list.length >= rule.maxFiles;
    return (
      <div
        className="border border-dashed rounded-4 p-3 text-center bg-light mb-2"
        role="button"
        tabIndex={0}
        aria-disabled={full || this.state.submitting}
        style={full || this.state.submitting ? { opacity: 0.6, cursor: "not-allowed" } : { cursor: "pointer" }}
        onClick={() => this.openFilePicker(fileType)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.openFilePicker(fileType);
          }
        }}
        onDragOver={this.handleDragOver}
        onDrop={(e) => this.handleDrop(e, fileType)}
      >
        <div className="text-secondary text-24 mb-2 icon">{fileType === "video" ? "\uf03d" : "\uf03e"}</div>
        <div className="text-14 text-dark">
          {t("drag_drop_files_here")} <span className="text-success fw-bold">
            {t("browse")}
            <input
              type="file"
              multiple
              hidden
              ref={(el) => { this.fileInputs[fileType] = el; }}
              onClick={(e) => e.stopPropagation()}
              disabled={full || this.state.submitting}
              onChange={(e) => this.handleBrowse(e, fileType)}
              accept={fileType === "video" ? "video/mp4,video/quicktime" : "image/*"}
            />
          </span>
        </div>
        <div className="text-12 text-secondary mt-1">
          {formatText(t(hintKey), { count: rule.maxFiles, size: toMB(rule.maxSize) })}
        </div>
      </div>
    );
  };

  renderPreviewItem = (f, idx, fileType) => (
    <div
      key={f.url}
      className="border rounded-3 p-2 position-relative bg-light"
      style={{ width: fileType === "video" ? "150px" : "100px" }}
    >
      <div
        role="button"
        title={t("delete")}
        className="position-absolute top-0 end-0 bg-danger text-white rounded-circle d-flex align-items-center justify-content-center pointer"
        style={{ width: "24px", height: "24px", transform: "translate(30%, -30%)", zIndex: 2 }}
        onClick={() => this.removeFile(idx, fileType)}
      >
        <span className="icon text-10">{"\uf00d"}</span>
      </div>
      {fileType === "video" ? (
        <video src={f.url} controls className="rounded-2 mb-1 w-100 bg-dark" style={{ height: "90px", objectFit: "contain" }} />
      ) : (
        <img src={f.url} alt={f.name} className="rounded-2 mb-1" style={{ width: "100%", height: "70px", objectFit: "cover" }} />
      )}
      <div className="text-10 text-secondary text-truncate" title={f.name}>{f.name}</div>
      <div className="text-10 text-secondary">{formatFileSize(f.size)}</div>
    </div>
  );

  renderAttachments = () => {
    const { images, videos } = this.state;
    return (
      <div className="bg-white rounded-4 shadow-sm p-3 mb-3">
        <label className="form-label text-13 text-secondary mb-2">{t("attachment_files")}</label>

        <label className="form-label text-13 text-secondary mb-1 d-block">
          {t("images")} ({images.length}/{IMAGE_RULE.maxFiles})
        </label>
        {this.renderDropzone("image")}
        {images.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3">{images.map((f, idx) => this.renderPreviewItem(f, idx, "image"))}</div>
        )}

        <label className="form-label text-13 text-secondary mb-1 d-block mt-2">
          {t("videos")} ({videos.length}/{VIDEO_RULE.maxFiles})
        </label>
        {this.renderDropzone("video")}
        {videos.length > 0 && (
          <div className="d-flex flex-wrap gap-2">{videos.map((f, idx) => this.renderPreviewItem(f, idx, "video"))}</div>
        )}
      </div>
    );
  };

  renderForm = () => {
    const {
      categories, submitting, upload_progress,
      create_category_id, create_service_type,
      create_problem_desc, create_reporter_name, create_reporter_phone,
    } = this.state;

    return (
      <>
        {this.renderAssetCard()}

        <div className="bg-white rounded-4 shadow-sm p-3 mb-3">
          <label className="form-label text-13 text-secondary">{t("req_category")} <span className="text-danger">*</span></label>
          <select
            className="form-select rounded-3 mb-3"
            value={create_category_id}
            onChange={(e) => {
              const item = categories.find((c) => c.uid === e.target.value);
              this.setState({ create_category_id: e.target.value, create_service_type: item?.service_type || null });
            }}
          >
            <option value="">{t("select_req_category")}</option>
            {categories.map((c) => (
              <option key={c.uid} value={c.uid}>{c.name_th}</option>
            ))}
          </select>

          {create_service_type ? (
            <div className="text-13 text-secondary mb-3">{t("service_type")}: {create_service_type.name_th}</div>
          ) : null}

          <label className="form-label text-13 text-secondary">{t("detailed_description")} <span className="text-danger">*</span></label>
          <textarea
            className="form-control rounded-3 mb-1"
            rows={4}
            maxLength={DESC_MAX_LENGTH}
            placeholder={t("describe_problem_placeholder")}
            value={create_problem_desc}
            onChange={(e) => this.setState({ create_problem_desc: e.target.value })}
          />
          <div className="d-flex justify-content-end text-secondary text-12 mb-3">
            <span>{create_problem_desc.length}/{DESC_MAX_LENGTH}</span>
          </div>

          <label className="form-label text-13 text-secondary">{t("reporter_name")} <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control rounded-3 mb-3"
            value={create_reporter_name}
            onChange={(e) => this.setState({ create_reporter_name: e.target.value })}
          />

          <label className="form-label text-13 text-secondary">{t("reporter_phone")} <span className="text-danger">*</span></label>
          <input
            type="tel"
            className="form-control rounded-3"
            value={create_reporter_phone}
            onChange={(e) => this.setState({ create_reporter_phone: e.target.value })}
          />
        </div>

        {this.renderAttachments()}

        <button
          className="btn btn-success w-100 py-3 fw-bold rounded-3"
          disabled={submitting}
          onClick={this.handleSubmit}
        >
          {submitting && upload_progress ? upload_progress : t("submit_request")}
        </button>
      </>
    );
  };

  render() {
    const { view_state } = this.state;

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <div className="p-3" style={{ maxWidth: "520px", marginLeft: "auto", marginRight: "auto" }}>
          <div className="rounded-4 p-3 mb-3 text-white" style={{ background: "linear-gradient(90deg, #0f766e, #14b8a6)" }}>
            <h5 className="fw-bold mb-1">{t("repair_request_page_title")}</h5>
            <div className="text-13 opacity-75" style={{ whiteSpace: "pre-line" }}>{t("repair_request_page_subtitle")}</div>
          </div>

          {view_state === "invalid" && this.renderInvalid()}
          {view_state === "pending" && this.renderPending()}
          {view_state === "success" && this.renderSuccess()}
          {view_state === "form" && this.renderForm()}
        </div>
      </div>
    );
  }
}
